import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClientConfig } from '@/hooks/useClients';
import { useMarketOverview } from '@/hooks/useMarket';
import { usePortfolioHealth } from '@/hooks/usePortfolio';
import { useLanguage } from '@/contexts/LanguageContext';
import { LoadingPage, LoadingCard } from '@/components/ui/LoadingSpinner';
import { ErrorPage } from '@/components/ui/ErrorMessage';
import { MetricTooltip } from '@/components/ui/Tooltip';
import { SectionHeaderWithFreshness, PROVENANCE_PRESETS } from '@/components/ui';
import { ExportCenter } from '@/components/export';

/**
 * ClientDashboard - Main overview page for client simulators
 *
 * UX Improvements implemented:
 * - UX-001: Metric labels (volume vs revenue vs consumers)
 * - UX-002: Data source/fidelity/recency indicators
 * - UX-003: Progressive disclosure (collapsed sections by default)
 */

function formatCurrency(value: number, currency: string = 'USD'): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(1)}%`;
}

export function ClientDashboard() {
  const { clientId } = useParams();
  const { t, language: locale } = useLanguage();
  const configQuery = useClientConfig(clientId);
  const marketQuery = useMarketOverview(clientId);
  const portfolioQuery = usePortfolioHealth(clientId);

  // Progressive disclosure state - show less by default
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllModules, setShowAllModules] = useState(false);

  if (configQuery.isLoading) {
    return <LoadingPage message="Loading client data..." />;
  }

  if (configQuery.error) {
    return <ErrorPage error={configQuery.error} onRetry={() => configQuery.refetch()} />;
  }

  const config = configQuery.data;
  const market = marketQuery.data;
  const portfolio = portfolioQuery.data;

  // Determine visible items based on progressive disclosure
  const visibleBrands = showAllBrands
    ? market?.top_brands
    : market?.top_brands?.slice(0, 3);
  const visibleModules = showAllModules
    ? config?.enabled_modules
    : config?.enabled_modules?.slice(0, 4);

  // Prepare data for export
  const exportData = {
    market: market ? {
      totalValue: market.market.total_value,
      currency: market.market.currency,
      growthRate: market.market.growth_rate,
      topBrands: market.top_brands || [],
    } : undefined,
    portfolio: portfolio ? {
      skuCount: portfolio.sku_count,
      hhi: portfolio.hhi,
      products: [], // Would need to fetch full product list for detailed export
    } : undefined,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with title and export button */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">
          {config?.client_name || clientId} Overview
        </h1>
        <ExportCenter
          clientName={config?.client_name || clientId || 'Client'}
          data={exportData}
          pdfElementId="dashboard-content"
        />
      </div>

      {/* Dashboard content for PDF export */}
      <div id="dashboard-content">
      {/* KPI Summary Cards with metric labels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {marketQuery.isLoading ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : (
          <>
            {/* Market Value - Revenue metric */}
            <div className="card p-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="metric-label">{t.marketValue}</span>
                <MetricTooltip metric="shareRevenue" position="bottom" locale={locale} />
              </div>
              <div className="metric-value">
                {market ? formatCurrency(market.market.total_value, market.market.currency) : '-'}
              </div>
              <div className="text-xs text-text-tertiary mt-1">
                {locale === 'es' ? 'Ingresos totales' : 'Total revenue'}
              </div>
            </div>

            {/* Your Share - Volume metric */}
            <div className="card p-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="metric-label">{t.yourShare}</span>
                <MetricTooltip metric="shareVolume" position="bottom" locale={locale} />
              </div>
              <div className="metric-value">
                {portfolio ? `${(portfolio.hhi * 100).toFixed(1)}%` : '-'}
              </div>
              <div className="text-xs text-text-tertiary mt-1">
                {locale === 'es' ? 'Volumen de categoría' : 'Category volume'}
              </div>
            </div>

            {/* SKUs Active */}
            <div className="card p-4">
              <div className="metric-label mb-1">{t.skusActive}</div>
              <div className="metric-value">
                {portfolio?.sku_count || '-'}
              </div>
              <div className="text-xs text-text-tertiary mt-1">
                {locale === 'es' ? 'Productos activos' : 'Active products'}
              </div>
            </div>

            {/* YoY Growth - Volume metric */}
            <div className="card p-4">
              <div className="flex items-center gap-1 mb-1">
                <span className="metric-label">{t.yoyGrowth}</span>
                <MetricTooltip metric="growthRate" position="bottom" locale={locale} />
              </div>
              <div className={`metric-value ${market && market.market.growth_rate >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                {market ? formatPercent(market.market.growth_rate) : '-'}
              </div>
              <div className="text-xs text-text-tertiary mt-1">
                {locale === 'es' ? 'Volumen YoY' : 'Volume YoY'}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Enabled Modules - with data freshness indicator */}
        <div className="card p-6">
          <SectionHeaderWithFreshness
            title={t.enabledModules}
            provenance={PROVENANCE_PRESETS.clientData(new Date())}
            locale={locale}
          />
          {config?.enabled_modules ? (
            <>
              <div className="space-y-2">
                {visibleModules?.map((module: any) => (
                  <div key={module.name} className="flex items-center justify-between p-2 bg-surface-600 rounded">
                    <span className="text-white capitalize">{module.name.replace(/_/g, ' ')}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      module.tier === 'advanced' ? 'bg-primary-500/20 text-primary-400' : 'bg-surface-500 text-text-secondary'
                    }`}>
                      {module.tier === 'advanced' ? t.advanced : t.basic}
                    </span>
                  </div>
                ))}
              </div>
              {/* Progressive disclosure - show more/less */}
              {config.enabled_modules.length > 4 && (
                <button
                  onClick={() => setShowAllModules(!showAllModules)}
                  className="mt-3 text-sm text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${showAllModules ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                  {showAllModules
                    ? (locale === 'es' ? 'Ver menos' : 'See less')
                    : (locale === 'es' ? `Ver ${config.enabled_modules.length - 4} más` : `See ${config.enabled_modules.length - 4} more`)}
                </button>
              )}
            </>
          ) : (
            <p className="text-text-secondary">{t.noModulesConfigured}</p>
          )}
        </div>

        {/* Top Brands - with data freshness and progressive disclosure */}
        <div className="card p-6">
          <SectionHeaderWithFreshness
            title={t.topBrands}
            provenance={PROVENANCE_PRESETS.nielsenRecent(new Date('2023-11-15'))}
            locale={locale}
          >
            {/* Metric label clarification */}
            <span className="text-xs text-text-tertiary flex items-center gap-1">
              {locale === 'es' ? 'Participación Vol' : 'Share (Vol)'}
              <MetricTooltip metric="brandShare" position="left" locale={locale} />
            </span>
          </SectionHeaderWithFreshness>
          {marketQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
              <div className="h-8 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : market?.top_brands ? (
            <>
              <div className="space-y-2">
                {visibleBrands?.map((brand, index) => (
                  <div key={brand.name} className="flex items-center justify-between p-2 bg-surface-600 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-text-secondary text-sm">#{index + 1}</span>
                      <span className="text-white">{brand.name}</span>
                    </div>
                    <span className="text-text-secondary">{(brand.share * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              {/* Progressive disclosure - show more/less */}
              {market.top_brands.length > 3 && (
                <button
                  onClick={() => setShowAllBrands(!showAllBrands)}
                  className="mt-3 text-sm text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${showAllBrands ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                  {showAllBrands
                    ? (locale === 'es' ? 'Ver menos' : 'See less')
                    : (locale === 'es' ? `Ver ${market.top_brands.length - 3} más` : `See ${market.top_brands.length - 3} more`)}
                </button>
              )}
            </>
          ) : (
            <p className="text-text-secondary">{t.noBrandData}</p>
          )}
        </div>
      </div>
      </div>{/* End dashboard-content */}
    </div>
  );
}
